// Hierarchical query keys for efficient invalidation
export const queryKeys = {
  timetable: {
    all: ['timetable'] as const,
    lists: () => [...queryKeys.timetable.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.timetable.lists(), filters] as const,
    details: () => [...queryKeys.timetable.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.timetable.details(), id] as const,
    // Multi-section query keys
    multiSection: (classId: string, academicYear: string, date?: string) =>
      [...queryKeys.timetable.all, 'multi-section', classId, academicYear, date] as const,
    teacherSuggestions: (sectionId: string, schedulePeriodId: string, day: string, subjectId: string) =>
      [...queryKeys.timetable.all, 'suggestions', sectionId, schedulePeriodId, day, subjectId] as const,
  },
  teachers: {
    all: ['teachers'] as const,
    lists: () => [...queryKeys.teachers.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.teachers.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.teachers.all, 'detail', id] as const,
    // Teacher associations query keys
    associations: (teacherId: string, academicYear?: string) =>
      [...queryKeys.teachers.all, 'associations', teacherId, academicYear] as const,
  },
  subjects: {
    all: ['subjects'] as const,
    lists: () => [...queryKeys.subjects.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.subjects.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.subjects.all, 'detail', id] as const,
  },
  classSubjects: {
    all: ['classSubjects'] as const,
    lists: () => [...queryKeys.classSubjects.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.classSubjects.lists(), filters] as const,
    forClass: (classId: string, schoolId: string) =>
      [...queryKeys.classSubjects.all, 'forClass', classId, schoolId] as const,
  },
  classes: {
    all: ['classes'] as const,
    lists: () => [...queryKeys.classes.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.classes.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.classes.all, 'detail', id] as const,
  },
  classGroups: {
    all: ['classGroups'] as const,
    list: (schoolId: string) => [...queryKeys.classGroups.all, 'list', schoolId] as const,
  },
  classSections: {
    all: ['classSections'] as const,
    lists: () => [...queryKeys.classSections.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.classSections.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.classSections.all, 'detail', id] as const,
  },
  classSchedules: {
    all: ['classSchedules'] as const,
    lists: () => [...queryKeys.classSchedules.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.classSchedules.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.classSchedules.all, 'detail', id] as const,
  },
  schedulePeriods: {
    all: ['schedulePeriods'] as const,
    lists: () => [...queryKeys.schedulePeriods.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.schedulePeriods.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.schedulePeriods.all, 'detail', id] as const,
    bySchedule: (scheduleId: string) =>
      [...queryKeys.schedulePeriods.all, 'bySchedule', scheduleId] as const,
  },
  absences: {
    all: ['absences'] as const,
    lists: () => [...queryKeys.absences.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.absences.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.absences.all, 'detail', id] as const,
  },
  assignments: {
    all: ['assignments'] as const,
    lists: () => [...queryKeys.assignments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.assignments.lists(), filters] as const,
    detail: (id: string) =>
      [...queryKeys.assignments.all, 'detail', id] as const,
    forDate: (schoolId: string, date: string) =>
      [...queryKeys.assignments.all, 'forDate', schoolId, date] as const,
  },
  unavailability: {
    all: ['unavailability'] as const,
    lists: () => [...queryKeys.unavailability.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.unavailability.lists(), filters] as const,
    detail: (id: string) =>
      [...queryKeys.unavailability.all, 'detail', id] as const,
  },
  academicYears: {
    all: ['academicYears'] as const,
    lists: () => [...queryKeys.academicYears.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.academicYears.lists(), filters] as const,
    detail: (id: string) =>
      [...queryKeys.academicYears.all, 'detail', id] as const,
    active: (schoolId: string) =>
      [...queryKeys.academicYears.all, 'active', schoolId] as const,
  },
  schools: {
    all: ['schools'] as const,
    lists: () => [...queryKeys.schools.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.schools.lists(), filters] as const,
    detail: (schoolId: string) =>
      [...queryKeys.schools.all, 'detail', schoolId] as const,
    // School rules query keys
    rules: (schoolId: string) =>
      [...queryKeys.schools.all, 'rules', schoolId] as const,
  },
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    detail: (userId: string) =>
      [...queryKeys.users.all, 'detail', userId] as const,
  },
  rooms: {
    all: ['rooms'] as const,
    lists: () => [...queryKeys.rooms.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.rooms.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.rooms.all, 'detail', id] as const,
  },
  scheduleOverrides: {
    all: ['scheduleOverrides'] as const,
    lists: () => [...queryKeys.scheduleOverrides.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.scheduleOverrides.lists(), filters] as const,
    detail: (id: string) =>
      [...queryKeys.scheduleOverrides.all, 'detail', id] as const,
  },
};
